import React, { createContext, useContext, useState, ReactNode } from "react"
import { getCurrentUserUsage } from "src/services/api/user"
import { useLogger } from "./LoggerProvider"
import UserService, { UserRole } from "src/UserService"

interface UsageLimitContextType {
  usedCharacters: number
  userLimit: number | null // null means unlimited
  checkUsageLimits: () => Promise<void>
  isTranslateAccessRestricted: () => boolean
}

const UsageLimitContext = createContext<UsageLimitContextType | undefined>(undefined)

interface UsageLimitProviderProps {
  children: ReactNode
}

export const UsageLimitProvider: React.FC<UsageLimitProviderProps> = ({ children }) => {
  const { logger } = useLogger()
  const [isLimitReached, setIsLimitReached] = useState(false)
  const [usedCharacters, setUsedCharacters] = useState(0)
  const [userLimit, setUserLimit] = useState<number | null>(100000)

  const checkUsageLimits = async () => {
    try {
      const usage = await getCurrentUserUsage()
      const totalUsed = usage.aiTextCount + usage.aiFileCount
      const limit = usage.translationLimit

      setUsedCharacters(totalUsed)
      setUserLimit(limit)
      // If limit is null (unlimited), never reach the limit
      setIsLimitReached(limit !== null && totalUsed >= limit)
    } catch (error) {
      logger.error("Failed to fetch user usage", error)
    }
  }

  // Check if translate access is restricted: when user does NOT have BUYER role OR limit is reached
  // This is used for both showing the dialog and disabling translate/send buttons
  const isTranslateAccessRestricted = (): boolean => {
    const hasBuyerRole = UserService.hasRoles([UserRole.BUYER])
    return !hasBuyerRole || isLimitReached
  }

  return (
    <UsageLimitContext.Provider value={{
      checkUsageLimits,
      usedCharacters,
      userLimit,
      isTranslateAccessRestricted
    }}>
      {children}
    </UsageLimitContext.Provider>
  )
}

export const useUsageLimit = () => {
  const context = useContext(UsageLimitContext)
  if (!context) {
    throw new Error("useUsageLimit must be used within UsageLimitProvider")
  }
  return context
}

